import { useState } from "react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { Send, Check, Instagram, Mail } from "lucide-react";

const COLLAB_TYPES = ["Sponsored Content", "Event", "Recipe Development", "Consulting", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", brand: "", email: "", collab_type: "Sponsored Content", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Your message is on its way. We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="contact-page">
      <div className="max-w-2xl">
        <p className="eyebrow">Brand Collaborations</p>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl md:text-6xl font-bold text-cream leading-[1.03]">
          Let's make<br /><span className="italic font-serif-editor text-pink-brand">something together.</span>
        </h1>
        <p className="mt-4 text-cream/70 font-body">
          Sponsored features, product placements, tastings, event partnerships, or a bespoke recipe — we're open to a good idea told well.
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-12 gap-8">
        <form onSubmit={submit} className="md:col-span-8 card-cream p-6 sm:p-8 space-y-4" data-testid="contact-form">
          {done ? (
            <div className="py-10 text-center">
              <div className="h-14 w-14 rounded-full bg-pink text-cream mx-auto flex items-center justify-center">
                <Check size={24} />
              </div>
              <p className="mt-4 font-serif-display text-2xl font-bold text-cream">Message received.</p>
              <p className="mt-1 text-cream/60 font-body">We reply within 3-4 working days. Meanwhile, a cocktail on us — <a className="text-pink-brand underline" href="https://instagram.com/sip_alchemy_" target="_blank" rel="noopener noreferrer">@sip_alchemy_</a>.</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your Name" value={form.name} onChange={update("name")} testId="contact-name" required />
                <Field label="Brand / Agency" value={form.brand} onChange={update("brand")} testId="contact-brand" />
              </div>
              <Field label="Email" type="email" value={form.email} onChange={update("email")} testId="contact-email" required />
              <div>
                <label className="eyebrow block mb-2">Collaboration Type</label>
                <div className="flex flex-wrap gap-2">
                  {COLLAB_TYPES.map(t => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setForm({ ...form, collab_type: t })}
                      className={`h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${form.collab_type === t ? "bg-ink text-cream border-ink" : "bg-ink-panel text-cream border-cream/10 hover:border-cream/30"}`}
                      data-testid={`collab-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2">Message</label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Tell us about the project, the timeline, and the vibe you're chasing."
                  className="w-full rounded-2xl border border-cream/10 bg-ink-panel p-4 text-cream font-body focus:outline-none focus:border-pink-brand resize-none"
                  data-testid="contact-message"
                  required
                />
              </div>
              <button type="submit" disabled={loading} className="btn-pill btn-pink w-full sm:w-auto" data-testid="contact-submit">
                {loading ? "Sending…" : <>Send message <Send size={16} /></>}
              </button>
            </>
          )}
        </form>

        <aside className="md:col-span-4 space-y-4">
          <div className="card-ink p-6 rounded-2xl">
            <p className="eyebrow" style={{ color: "#F9C0D2" }}>Direct</p>
            <p className="mt-2 font-serif-display text-xl font-bold text-cream">DMs open</p>
            <p className="text-cream/70 text-sm mt-2">Fastest way to reach the team is Instagram.</p>
            <a href="https://instagram.com/sip_alchemy_" target="_blank" rel="noopener noreferrer" className="btn-pill btn-pink mt-4 inline-flex" data-testid="contact-instagram-link">
              <Instagram size={16} /> @sip_alchemy_
            </a>
          </div>
          <div className="card-cream p-6 rounded-2xl">
            <p className="eyebrow">Email</p>
            <a href="mailto:sipalchemy.collabs@gmail.com" className="mt-2 font-serif-display text-xl font-bold text-cream flex items-center gap-2 hover:text-pink-brand transition-colors break-all" data-testid="contact-email-link">
              <Mail size={18} className="text-pink-brand flex-shrink-0" /> sipalchemy.collabs@gmail.com
            </a>
            <p className="text-cream/60 text-sm mt-3">For decks, briefs, and formal proposals.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required = false }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full h-12 rounded-full border border-cream/10 bg-ink-panel px-5 text-cream font-body focus:outline-none focus:border-pink-brand"
        data-testid={testId}
      />
    </div>
  );
}
